// src/components/common/Header.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { ReactComponent as Logo } from '../../assets/logo.svg';

/**
 * Header component with navigation and mobile menu functionality.
 * Manages its own menu open/close state.
 * @return {React.ReactElement} The Header component.
 */
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleNavLinkClick = (targetId) => {
    setIsMenuOpen(false);
    const headerOffset = 80; // Approximate height of your fixed header in pixels
    if (targetId === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.getElementById(targetId);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - headerOffset; // Subtract header height

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }
  };

  const navLinks = [
    { label: 'Продукция', id: 'продукция' },
    { label: 'Преимущества', id: 'преимущества' },
    { label: 'Контакты', id: 'контакты' },
  ];

  return (
    <nav className="fixed top-0 w-full bg-white shadow-md z-50">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="text-2xl font-bold text-gray-800 flex items-center"
            onClick={() => handleNavLinkClick('/')}
          >
            <Logo className="h-8 w-auto mr-2" />
          </Link>

          {/* Centered navigation links */}
          <div className="hidden md:flex flex-1 justify-center items-center">
            <div className="flex space-x-8">
              <Link
                to="/"
                className="text-gray-600 font-medium cursor-pointer hover:text-orange-500 transition-colors duration-100"
                onClick={() => handleNavLinkClick('/')}
              >
                Главная
              </Link>
              {navLinks.map((item) => (
                <Link
                  key={item.id}
                  to={`/#${item.id}`}
                  className="text-gray-600 font-medium cursor-pointer hover:text-orange-500 transition-colors duration-100"
                  onClick={() => handleNavLinkClick(item.id)}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <a
              href="https://api.whatsapp.com/send?phone=77785929644"
              className="bg-orange-500 text-white px-6 py-2 rounded-full font-semibold shadow-lg cursor-pointer hover:bg-orange-600 transition-colors duration-100"
              target="_blank"
              rel="noopener noreferrer"
            >
              Связаться в WhatsApp
            </a>
          </div>

          <button
            className="md:hidden text-gray-800 cursor-pointer hover:text-orange-500 transition-colors duration-100"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="md:hidden bg-white shadow-lg">
          <div className="container mx-auto px-6 py-4 space-y-4">
            <Link
              to="/"
              className="block text-gray-600 cursor-pointer hover:text-orange-500 transition-colors duration-100"
              onClick={() => handleNavLinkClick('/')}
            >
              Главная
            </Link>
            {navLinks.map((item) => (
              <Link
                key={item.id}
                to={`/#${item.id}`}
                className="block text-gray-600 cursor-pointer hover:text-orange-500 transition-colors duration-100"
                onClick={() => handleNavLinkClick(item.id)}
              >
                {item.label}
              </Link>
            ))}
            <a
              href="https://api.whatsapp.com/send?phone=77785929644"
              className="block bg-orange-500 text-white px-6 py-2 rounded-full font-semibold text-center cursor-pointer hover:bg-orange-600 transition-colors duration-100"
              target="_blank"
              rel="noopener noreferrer"
            >
              Написать в WhatsApp
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Header;
